import type { ZenObservable } from 'zen-observable-ts';
import { graphql, subscribe } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { decompressIfNeededThenJsonParse } from '@/woLint/services/dataset.common';
import type * as A from '@/api';
import type { BedrockMessage } from '@/utils/beemAgentTypes';

const ns = 'app.services.insightTurn';

// A turn with no update for this long is treated as failed: the connector always ends a turn it
// is running before the lambda's 15-minute limit, so silence past it means the worker died.
const STALE_AFTER_MS = 16 * 60 * 1000;

export type InsightTurnStatus = 'RUNNING' | 'DONE' | 'ERROR' | 'CANCELLED';

// One tool call the agent made while answering: a SQL query or a web page it read.
export type InsightTurnStep = {
  toolUseId: string;
  name: 'RedshiftQuery' | 'WebBrowser' | string;
  status: 'running' | 'success' | 'error';
  // What the model wrote just before calling the tool, e.g. "Let me pull last quarter's revenue."
  reasoning?: string;
  input?: { sql_statement?: string; action?: string; url?: string } & Record<string, unknown>;
  // What a tool the app runs returned: a dashboard's link, or how many monitoring events it read.
  output?: InsightToolOutput;
};

export type InsightToolOutput = {
  dashboardId?: string;
  // CreateDashboard: 'generating' for a build, 'ready' for a copy (made at once).
  status?: string;
  title?: string;
  url?: string | null;
  count?: number;
  error?: string;
  // GetDataset / ProposeDatasetChange / ProposeDatasetTest
  kind?: 'edit' | 'create' | 'test';
  datasetName?: string;
  passesToday?: boolean;
  datasetId?: string | null;
  name?: string;
  sql?: string;
  summary?: string;
  columns?: string[];
  // OfferChoices
  question?: string;
  options?: string[];
  // WriteDashboard / ReplaceCards
  problems?: string[];
  // ConnectSource
  service?: string;
  iconUrl?: string | null;
};

type InsightTurnInput = A.GetInsightTurnQuery['getInsightTurn'];

export type StartInsightTurnInput = Pick<
  A.StartInsightConverseInput,
  | 'workspaceId'
  | 'datasets'
  | 'prompt'
  | 'messages'
  | 'organizationContext'
  | 'sessionId'
  | 'linkBase'
  | 'profile'
  | 'dashboardId'
  | 'focusCards'
  | 'conversationId'
  | 'attachments'
>;

const parseSteps = (steps: unknown): InsightTurnStep[] => {
  try {
    // AWSJSON arrives as a JSON string.
    const parsed = typeof steps === 'string' ? JSON.parse(steps) : steps;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    logger.error(`${ns}.parseSteps`, e);
    return [];
  }
};

/**
 * One insights question while the agent answers it (BEEM-1448). The connector runs the turn in the
 * background and pushes a snapshot of this row on every step and every ~300 ms of streamed text,
 * so each snapshot is a complete state to render, never a delta to apply.
 */
export default class InsightTurn {
  readonly id: string;
  // The conversation's memory session, as the turn was started with it.
  readonly sessionId: string | null;
  readonly status: InsightTurnStatus;
  readonly answerText: string;
  readonly reasoningText: string;
  readonly steps: InsightTurnStep[];
  readonly memoryCount: number;
  readonly error?: string;
  readonly updatedAt?: string;

  constructor(input: InsightTurnInput) {
    this.id = input.id;
    this.sessionId = input.sessionId ?? null;
    this.status = input.status as InsightTurnStatus;
    this.answerText = input.answerText ?? '';
    this.reasoningText = input.reasoningText ?? '';
    this.steps = parseSteps(input.steps);
    this.memoryCount = input.memoryCount ?? 0;
    this.error = input.error;
    this.updatedAt = input.updatedAt;
  }

  get isTerminal(): boolean {
    return this.status !== 'RUNNING';
  }

  // Still RUNNING, but not updated for longer than any live turn can go without an update.
  get isStale(): boolean {
    if (this.isTerminal || !this.updatedAt) return false;
    return Date.now() - new Date(this.updatedAt).getTime() > STALE_AFTER_MS;
  }

  // The same turn in another state, for outcomes decided in the app: a stop the user asked for,
  // or a turn that went stale.
  withStatus(status: InsightTurnStatus, error?: string): InsightTurn {
    return new InsightTurn({
      id: this.id,
      userId: '',
      workspaceId: '',
      status: status as A.InsightTurnStatus,
      answerText: this.answerText,
      reasoningText: this.reasoningText,
      steps: JSON.stringify(this.steps),
      memoryCount: this.memoryCount,
      error: error ?? this.error,
      updatedAt: this.updatedAt,
    });
  }

  // The turn as it is when it has just been started, before the first snapshot arrives.
  static started(id: string): InsightTurn {
    return new InsightTurn({
      id,
      userId: '',
      workspaceId: '',
      status: 'RUNNING' as A.InsightTurnStatus,
    });
  }

  static async start(input: StartInsightTurnInput): Promise<string> {
    const { id } = await graphql('beemAgentStartInsightConverse', { query: input });
    return id;
  }

  static async getById(id: string): Promise<InsightTurn> {
    return new InsightTurn(await graphql('getInsightTurn', { id }));
  }

  // The full conversation once the turn is DONE, to replace the stored history with.
  static async getMessages(id: string): Promise<BedrockMessage[]> {
    const { messages } = await graphql('beemAgentGetInsightTurnMessages', { query: { id } });
    return decompressIfNeededThenJsonParse(messages) as BedrockMessage[];
  }

  static async cancel(id: string): Promise<InsightTurnStatus> {
    const { status } = await graphql('beemAgentCancelInsightTurn', { query: { id } });
    return status as InsightTurnStatus;
  }

  // Snapshots of one turn. The subscription is owner-scoped (userId) and carries every turn of
  // that user, so it is filtered to this id here. The returned handle MUST be unsubscribed once the
  // turn is terminal or the view goes away: AppSync bills open connections by the minute.
  static subscribeToUpdates(
    userId: string,
    id: string,
    onUpdate: (turn: InsightTurn) => void,
  ): ZenObservable.Subscription | null {
    try {
      return subscribe('onUpdateInsightTurn', { userId }, (record) => {
        if (record?.id === id) onUpdate(new InsightTurn(record));
      });
    } catch (e) {
      // The caller also polls, so a subscription that cannot open only costs latency.
      logger.error(`${ns}.subscribeToUpdates`, e);
      return null;
    }
  }
}
